import { useState } from 'react'
import { X, GripVertical, ChevronUp, ChevronDown, Trash2 } from 'lucide-react'

/**
 * MediaQueue - Queue Panel with Drag & Drop Reordering
 *
 * Features:
 * - Display list of queued media items
 * - Drag & drop to reorder (admins only)
 * - Remove items from queue
 * - Show who added each item
 * - Preview thumbnails
 * - Position numbers
 * - Clear all button (admins only)
 *
 * @param {Array} queue - Array of queue items from useMediaSession
 * @param {Function} onRemove - Remove item callback (itemId)
 * @param {Function} onReorder - Reorder callback (itemId, newPosition)
 * @param {Function} onClearAll - Clear all items callback
 * @param {Boolean} canControl - Whether user can reorder/remove
 * @param {Function} onClose - Close queue panel callback
 */
export default function MediaQueue({
  queue = [],
  onRemove,
  onReorder,
  onClearAll,
  canControl = false,
  onClose
}) {
  const [draggedItem, setDraggedItem] = useState(null)
  const [draggedOver, setDraggedOver] = useState(null)

  // Format duration (seconds to MM:SS)
  const formatDuration = (seconds) => {
    if (!seconds) return null
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Get media type icon
  const getMediaTypeIcon = (type) => {
    switch (type) {
      case 'youtube':
        return '▶️'
      case 'video':
        return '🎥'
      case 'audio':
        return '🎵'
      case 'image':
        return '🖼️'
      default:
        return '🔗'
    }
  }

  // Drag handlers for reordering
  const handleDragStart = (e, item, index) => {
    if (!canControl) return
    setDraggedItem({ item, index })
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, index) => {
    if (!canControl) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDraggedOver(index)
  }

  const handleDragLeave = () => {
    setDraggedOver(null)
  }

  const handleDrop = (e, dropIndex) => {
    if (!canControl || !draggedItem) return
    e.preventDefault()

    const { item, index: dragIndex } = draggedItem

    if (dragIndex !== dropIndex) {
      // Reorder: update position in database
      onReorder?.(item.id, dropIndex)
    }

    setDraggedItem(null)
    setDraggedOver(null)
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
    setDraggedOver(null)
  }

  // Move item up/down (alternative to drag-drop)
  const handleMoveUp = (item, currentIndex) => {
    if (currentIndex > 0) {
      onReorder?.(item.id, currentIndex - 1)
    }
  }

  const handleMoveDown = (item, currentIndex) => {
    if (currentIndex < queue.length - 1) {
      onReorder?.(item.id, currentIndex + 1)
    }
  }

  return (
    <div className="glass-strong rounded-xl p-4 w-full lg:w-96 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gjyl-slate-700">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-white">Queue</h3>
          <span className="px-2 py-0.5 bg-gjyl-indigo-500/20 text-gjyl-indigo-400 rounded-full text-xs font-medium">
            {queue.length}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Clear All (admins only) */}
          {canControl && queue.length > 0 && (
            <button
              onClick={onClearAll}
              className="text-xs text-red-400 hover:text-red-300 transition-colors"
            >
              Clear All
            </button>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gjyl-slate-700 rounded transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Queue Items */}
      <div className="flex-1 overflow-y-auto scrollbar-gjyl space-y-2">
        {queue.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-3 opacity-30">📝</div>
            <p className="text-slate-500 text-sm">Queue is empty</p>
            <p className="text-slate-600 text-xs mt-1">
              Add media to start building your playlist
            </p>
          </div>
        ) : (
          queue.map((item, index) => (
            <div
              key={item.id}
              draggable={canControl}
              onDragStart={(e) => handleDragStart(e, item, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`
                flex items-center gap-3 p-3 rounded-lg
                transition-all duration-200
                ${draggedItem?.index === index
                  ? 'opacity-50 scale-95'
                  : 'opacity-100 scale-100'
                }
                ${draggedOver === index
                  ? 'bg-gjyl-indigo-500/20 border-2 border-gjyl-indigo-500'
                  : 'bg-gjyl-slate-700/30 hover:bg-gjyl-slate-700/50 border-2 border-transparent'
                }
                ${canControl ? 'cursor-grab active:cursor-grabbing' : ''}
              `}
            >
              {/* Drag Handle + Position */}
              <div className="flex items-center gap-2 text-slate-500">
                {canControl && (
                  <GripVertical className="w-4 h-4" />
                )}
                <span className="text-sm font-semibold w-6 text-center">
                  {index + 1}
                </span>
              </div>

              {/* Thumbnail */}
              <div className="w-16 h-16 rounded bg-gjyl-slate-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                {item.thumbnail_url ? (
                  <img
                    src={item.thumbnail_url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-2xl">
                    {getMediaTypeIcon(item.platform)}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {item.title || item.video_url}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-slate-400 capitalize">
                    {item.platform}
                  </p>
                  {item.duration && (
                    <>
                      <span className="text-slate-600">•</span>
                      <p className="text-xs text-slate-400">
                        {formatDuration(item.duration)}
                      </p>
                    </>
                  )}
                </div>
                {item.adder && (
                  <p className="text-xs text-slate-500 mt-1">
                    Added by @{item.adder.username}
                  </p>
                )}
              </div>

              {/* Actions */}
              {canControl && (
                <div className="flex flex-col gap-1">
                  {/* Move Up */}
                  <button
                    onClick={() => handleMoveUp(item, index)}
                    disabled={index === 0}
                    className="p-1 hover:bg-gjyl-indigo-500/20 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move Up"
                  >
                    <ChevronUp className="w-4 h-4 text-gjyl-indigo-400" />
                  </button>

                  {/* Move Down */}
                  <button
                    onClick={() => handleMoveDown(item, index)}
                    disabled={index === queue.length - 1}
                    className="p-1 hover:bg-gjyl-indigo-500/20 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move Down"
                  >
                    <ChevronDown className="w-4 h-4 text-gjyl-indigo-400" />
                  </button>
                </div>
              )}

              {/* Remove Button */}
              <button
                onClick={() => onRemove?.(item.id)}
                className="p-2 hover:bg-red-500/20 rounded transition-colors"
                title="Remove from queue"
              >
                <X className="w-4 h-4 text-red-400 hover:text-red-300" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Footer Info */}
      {canControl && queue.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gjyl-slate-700">
          <p className="text-xs text-slate-500 text-center">
            💡 Drag items to reorder • Click{' '}
            <X className="w-3 h-3 inline" /> to remove
          </p>
        </div>
      )}
    </div>
  )
}
